import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { PADDING } from '../utils/constants'
import { useTranslation } from 'react-i18next'
import { localeCodeOf } from '../../../../generic/utilities/customLanguage'

const ChartAxis = ({ bounds, dataPoints, textColor, format, t_, width, height }) => {
  const { i18n } = useTranslation()
  const fontStyle = {
    userSelect: 'none',
    fontFamily: 'Roboto, sans-serif'
  }

  const mapLanguageToLocale = (lang) => {
    switch (lang) {
      case 'es':
        return 'es-ES'
      case 'en':
        return 'en-GB'
      case 'pt':
        return 'pt-PT'
      case 'fr':
        return 'fr-FR'
      case 'ca':
        return 'ca-ES'
      case 'eus':
        return 'eu-ES'
      case 'lv':
        return 'lv-LV'
      case 'de':
        return 'de-DE'
      default:
        return 'en-GB'
    }
  }

  const uiLocale = mapLanguageToLocale(localeCodeOf(i18n?.language))
  const hour12 = false

  const getEmptyLabels = () => {
    return { start: '00:00', mid: '12:00', end: '23:59' }
  }

  const formatLabelDynamic = (ts, rangeMs) => {
    if (!ts) return ''
    const d = new Date(ts)
    if (rangeMs <= 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { hour: 'numeric', minute: '2-digit', hour12 }).format(d)
    }
    if (rangeMs <= 3 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { weekday: 'short', hour: 'numeric', minute: '2-digit', hour12 }).format(d)
    }
    if (rangeMs <= 14 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12 }).format(d)
    }
    if (rangeMs <= 60 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { month: 'short', day: '2-digit' }).format(d)
    }
    return d.toLocaleDateString('en-CA')
  }

  const emptyLabels = getEmptyLabels()
  const axisColor = 'white'
  const axisY = height - PADDING.b

  const renderXAxisRuler = () => {
    const segments = 12
    const xStart = PADDING.l
    const xEnd = width - PADDING.r
    const stepX = (xEnd - xStart) / segments

    let majorStep = 3
    if (width < 520) majorStep = 4
    if (width < 420) majorStep = 6

    let firstTs = null
    let lastTs = null
    if (dataPoints.length > 0) {
      firstTs = dataPoints[0]?.ts ?? null
      lastTs = dataPoints[dataPoints.length - 1]?.ts ?? null
    }

    const hasTs = !!firstTs && !!lastTs
    const rangeMs = hasTs ? Math.max(0, lastTs - firstTs) : 0

    return (
      <>
        {Array.from({ length: segments + 1 }).map((_, i) => {
          const x = xStart + i * stepX
          const isMajor = i % majorStep === 0 || i === segments
          const tickLen = isMajor ? 10 : 6
          const tickOpacity = isMajor ? 0.6 : 0.35
          let label = ''
          if (hasTs && isMajor) {
            const ts = firstTs + Math.round((i / segments) * rangeMs)
            label = formatLabelDynamic(ts, rangeMs)
          } else if (!hasTs && dataPoints.length > 0 && isMajor) {
            if (i === 0) label = dataPoints[0]?.label ?? ''
            else if (i === segments) label = dataPoints[dataPoints.length - 1]?.label ?? ''
            else if (i === Math.floor(segments / 2)) label = dataPoints[Math.floor(dataPoints.length / 2)]?.label ?? ''
          } else if (!hasTs && dataPoints.length === 0 && isMajor) {
            if (i === 0) label = emptyLabels.start
            else if (i === segments) label = emptyLabels.end
            else if (i === Math.floor(segments / 2)) label = emptyLabels.mid
          }

          const textAnchor = i === 0 ? 'start' : (i === segments ? 'end' : 'middle')

          return (
            <React.Fragment key={`xTick${i}`}>
              <line
                x1={x}
                y1={axisY}
                x2={x}
                y2={axisY + tickLen}
                stroke={axisColor}
                strokeWidth={1}
                opacity={tickOpacity}
              />
              {label
                ? (
                  <text
                    x={x}
                    y={height - 15}
                    fill={textColor}
                    fontSize='12'
                    fontWeight='600'
                    textAnchor={textAnchor}
                    style={fontStyle}
                  >
                    {label}
                  </text>
                  )
                : null}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  return (
    <>
      {/* Axis Lines */}
      {/* Y Axis Line */}
      <line
        x1={PADDING.l}
        y1={PADDING.t}
        x2={PADDING.l}
        y2={height - PADDING.b}
        stroke={axisColor}
        strokeWidth={1}
        opacity={0.5}
      />
      {/* X Axis Line */}
      <line
        x1={PADDING.l}
        y1={axisY}
        x2={width - PADDING.r}
        y2={axisY}
        stroke={axisColor}
        strokeWidth={1}
        opacity={0.5}
      />

      {/* Y Axis Labels */}
      <text
        x={PADDING.l - 12}
        y={PADDING.t + 6}
        fill={textColor}
        fontSize='12'
        fontWeight='700'
        textAnchor='end'
        style={fontStyle}
      >
        {Math.round(bounds.maxY)}
      </text>
      <text
        x={PADDING.l - 12}
        y={height - PADDING.b}
        fill={textColor}
        fontSize='12'
        fontWeight='700'
        textAnchor='end'
        style={fontStyle}
      >
        {Math.round(bounds.minY)}
      </text>

      {renderXAxisRuler()}
    </>
  )
}

ChartAxis.propTypes = {
  bounds: PropTypes.object.isRequired,
  dataPoints: PropTypes.array.isRequired,
  textColor: PropTypes.string.isRequired,
  format: PropTypes.string,
  t_: PropTypes.func,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired
}

export default memo(ChartAxis)
